/**
 * @fileOverview ViewStore for keyboard handling date picker from React
 *					component
 * @author jzetlen
 *
 */

import { partial } from 'underscore';
import moment from 'moment';
import trackLink from 'view/activity/trackLink';
import { isWithinRange } from './base';

/**
 * @function keyboardHandling
 * Factory for a keyboard handling date picker store. This is a View Store
 * function; for more information on the signature of these functions,
 * see stores/base.es6.
 */
export default function keyboardHandling({ minDate, maxDate, formats }) {
	// this outer closure is for utilities and state that will be common
	// to all stores, similar to static properties on a class.
	return function({ getState, updateState, onUpdate, publish }) {
		// this inner closure is for state that will be private to the
		// individual store created with each invocation of this function,
		// similar to private instance properties on a class.

		// set local state for the last keypress detected. this is used in the
		// onInputChange event to determine whether the user intends to delete
		// the current date and re-enter, and debounces the `tryUserEntered`
		// function under those circumstances.
		let lastKeypress = '';
		let keypressTimeout;
		let lastDate;

		// if an update takes place, reset keypress state
		onUpdate(() => {
			lastKeypress = '';
			clearTimeout(keypressTimeout);
		});
		return {
			onFocusInput: (event, { behavior, date }) => {
				//only update the state if the date is different than the last selected one or if it's initializing for the first time
				let { editing } = getState();
				if (lastDate !== date || (editing === undefined || editing === false)) {
					lastDate = date;
					trackLink(behavior + '_opencalendar');
					updateState({
						// set editing to "start" or "end"
						editing: behavior,
						// remove any hover state that may exist
						hoverAnchor: false,
						// show the month for the date in the selected input
						showMonth: date
					});
				}
			},
			onFocusOut: (event, props) => {
				//close the datepicker if it loses focus
				updateState({ editing: false, hoverAnchor: false });
			},
			onInputKeyDown: (event, props) => {
				// track last keypress in the local enclosed variable
				// that onInputChange can read
				lastKeypress = event.key;
				// this is tightly bound to the convenience `key` strings
				// that React input events provide.
				const up = lastKeypress === 'ArrowUp',
					down = lastKeypress === 'ArrowDown',
					enter = lastKeypress === 'Enter',
					tab = lastKeypress === 'Tab';

				// format and set intended input date
				formats.dateInput = formats.dateInput.replace(/&rlm;|\u200F/gi, '');
				const targetDate = cleanUpDateInput(event.target.value);
				const intendedDate = moment(targetDate, formats.dateInput, true);

				if (up || down) {
					event.preventDefault();
					if (!intendedDate.isValid()) {
						return;
					}
					// modify intendedDate in place (moment dates are mutable)
					// based on keypress
					if (up) {
						intendedDate.add(1, 'day');
					}
					if (down) {
						intendedDate.subtract(1, 'day');
					}
					// submit if valid
					tryUserEntered(intendedDate);
				}

				// publish on enter key
				// updates the state of the datepicker with new date
				// publishes the changes and closes the datepicker
				if (enter) {
					trackLink('manual_selected');
					tryUserEntered(intendedDate);
					const { startDate, endDate } = getState();
					publish(event, startDate, endDate);
					close(event);
				}

				// if tabbing, make sure we set the date to what the user entered
				if (tab) {
					tryUserEntered(intendedDate);
				}

			},
			onInputChange: (event, props) => {
				// try to make a moment date object out of the entered text
				// passing in false to enable forgiving parsing (allowing input MM/DD/YY or MM/DD/YYYY)
				formats.dateInput = formats.dateInput.replace(/&rlm;|\u200F/gi, '');
				const targetDate = cleanUpDateInput(event.target.value);
				const numOfDigits = targetDate.replace(/[^0-9]/g, '').length;
				const entered = numOfDigits === 6 ? moment(targetDate, formats.dateInput, true) : undefined;
				// debounce keypresses--cancel any pending tryUserEntered
				clearTimeout(keypressTimeout);
				// set a default delay of half second to allow people to finish entering dates before tryUserEntered
				let delay = 500;
				// if the user seems to be deleting, wait a second for them
				// to re-enter before tryUserEntered
				if (lastKeypress === 'Backspace' || lastKeypress === 'Delete') {
					delay = 1000;
				}
				keypressTimeout = setTimeout(
					partial(tryUserEntered, entered),
					delay
				);
			}
		};
		// closes the datepicker
		// remove focus on input by doing a blur
		function close (event) {
			updateState({ editing: false, hoverAnchor: false });
			event.target.blur();
		}
		// if this is a startDate and it is valid and prior to the endDate,
		// OR if this is an endDate and it is valid and later than startDate,
		// then update the view immediately with the new value.
		// this makes the datepicker instantly reactive to user entered dates,
		// and to up arrow/down arrow incrementing and decrementing, as
		// implemented in onKeyDown.
		function tryUserEntered(entered) {
			const { editing, startDate, endDate } = getState(),
			min = editing === 'end' ? startDate : minDate,
			max = editing === 'start' ? endDate : maxDate;
			if (entered.isValid() && isWithinRange({ minDate: min, maxDate: max }, entered)) {
				const validDate = entered.toDate();
				updateState({
					[editing + 'Date']: validDate,
					showMonth: validDate
				});
			}
		}

		function cleanUpDateInput(date) {
			return date.replace(/&rlm;|\u200F/gi, '');
		}
	};
}



// WEBPACK FOOTER //
// ./view/activity/dateRangePicker/stores/keyboardHandling.es6